import "@assets/css/bootstrap-grid.css";
import { Spinner } from "@components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { getInputField } from "@utils/function/index.function";
import { showToast, throwErrorAlert } from "@utils/helper/index.helper";
import { FormField } from "@utils/interface/form.interface";
import {
  UserVerifyOtpFormData,
  UserVerifyOtpSchema,
} from "@utils/interface/form.schema";
import { METHODS } from "@utils/interface/index.interface";
import { usePostData } from "@utils/query/index.query";
import { useResetPassword, useSelectionStore } from "@utils/states/index.state";
import { ChevronLeft } from "lucide-react";
import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";

const ResetOTPForm: React.FC = () => {
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<UserVerifyOtpFormData>({
    mode: "onChange",
    resolver: zodResolver(UserVerifyOtpSchema),
  });
  /* set default value */
  // useEffect(() => {}, []);

  const { mutate, isPending } = usePostData();
  const { email, setEmail, setOtp } = useResetPassword();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const urlEmail = searchParams.get("email");
    const urlOtp = searchParams.get("otp");

    // Set email from URL if available and not already set in store
    if (urlEmail && !email) {
      setEmail(urlEmail);
    }

    // Prefill OTP if available in URL
    if (urlOtp) {
      setValue("otp", urlOtp);
      setOtp(urlOtp);
    }
  }, [searchParams, setValue, setEmail, setOtp, email]);

  const handleMutate = (data: any) => {
    return {
      data,
      endpoint: endpoint["auth"]["verifyOtp"],
      method: METHODS.POST,
    };
  };

  const { mutate: resendOTP } = usePostData();
  const handleSuccess = async (response: any) => {
    const { data, status } = response;

    if (status !== 200) {
      return showToast({
        description: "Invalid OTP",
        type: "error",
        title: "Error",
      });
    }
    console.log(data, "success");
    showToast({
      description: "OTP Verified Successfully",
      type: "success",
      title: "Success",
    });
    navigate("/reset-password");
  };
  const { setIsFormPending } = useSelectionStore();

  const handleError = async (error: any) => {
    console.log(error, "error00");
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    setIsFormPending(false);
    const errorMessage = error.data.response.data;
    console.log(errorMessage, "Error Message");
    throwErrorAlert(errorMessage);
  };

  const onSubmit: SubmitHandler<UserVerifyOtpFormData> = (data) => {
    setOtp(data.otp);
    mutate(
      handleMutate({
        ...data,
        email: email,
      }),
      {
        onSuccess: ({ data, status }) => handleSuccess({ data, status }),
        onError: (data) => handleError({ data }),
      }
    );
  };
  const [timeLeft, setTimeLeft] = useState<number>(0);

  useEffect(() => {
    if (timeLeft > 0) {
      const timerId = setInterval(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearInterval(timerId);
    }
  }, [timeLeft]);
  const formSchema: FormField[] = [
    {
      name: "otp",
      label: "",
      colSpan: 12,
      type: "otp",
      length: 6,
      control: control,
      setValue: setValue,
      errors,
      className: `min-w-full w-full`,
      visable: true,
    },
    {
      type: "component",
      component: (
        <p className="my-4">
          Didn't receive the code?{" "}
          <button
            type="button"
            disabled={timeLeft > 0 ? true : false}
            onClick={() => {
              resendOTP(
                {
                  endpoint: endpoint["auth"]["getOtp"],
                  method: METHODS.POST,
                  data: {
                    email: email,
                  },
                },
                {
                  onSuccess: () => {
                    showToast({
                      description: "Code Resent Successfully",
                      type: "success",
                      title: "Success",
                    });
                    setTimeLeft(60);
                  },
                }
              );
            }}
            className={`text-[#1DA077] cursor-pointer ${
              timeLeft > 0 ? "opacity-50" : ""
            } font-semibold`}
          >
            {timeLeft > 0 ? `  Resend Code in ${timeLeft} sec` : " Resend Code"}
          </button>{" "}
          .
        </p>
      ),
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      type: "submit",
      value: "Next",
      className: "w-full h-[47px] mt-[32px]",
      colSpan: 12,
      variant: "default",
      endIcon: isPending ? <Spinner /> : null,
      disabled: isPending ? true : false,
      visable: true,
    },
    {
      type: "button",
      value: "Back to login ",
      className: "w-full h-[47px] mt-[4px] text-[#1DA077]",
      startIcon: <ChevronLeft />,
      colSpan: 12,
      onClick: () => navigate("/login"),
      variant: "ghost",
      disabled: isPending ? true : false,
      visable: true,
    },
  ];

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="row w-[424px] max-md:w-full max-md:p-4"
      >
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};

export default ResetOTPForm;
